"use client";

import * as React from "react";
import { ArrowUpRight, MapPin, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const projectCategories = [{
  label: "All Projects",
  count: "36+"
}, {
  label: "Kitchens",
  count: "14"
}, {
  label: "Bathrooms",
  count: "10"
}, {
  label: "Basements",
  count: "5"
}, {
  label: "Additions",
  count: "4"
}, {
  label: "Whole-Home",
  count: "3"
}];
const localAreas = ["Oakhurst", "Winnona Park", "Great Lakes", "Downtown Decatur"];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background pt-24 pb-16 sm:pt-32 sm:pb-20 lg:pt-36 lg:pb-24 border-b border-border">
      {/* Subtle architectural background grid accent */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
      backgroundSize: "32px 32px"
    }} />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-4xl">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="mb-5 flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-secondary px-3 py-1 text-xs font-semibold tracking-wider text-foreground uppercase">
              <Sparkles className="size-3.5 text-primary" />
              PORTFOLIO SHOWCASE
            </span>
            <span className="hidden sm:inline-block h-px w-12 bg-border" />
            <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
              <MapPin className="size-3.5 text-primary" />
              Decatur, Georgia & Neighboring Communities
            </span>
          </motion.div>

          {/* Primary Heading */}
          <motion.h1 variants={itemVariants} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-[1.1]">
            Our Decatur Remodeling Projects
          </motion.h1>

          {/* Architectural divider line */}
          <motion.div variants={itemVariants} className="mt-6 mb-6 h-0.5 w-24 bg-primary" />

          {/* Introductory Paragraph */}
          <motion.p variants={itemVariants} className="text-lg sm:text-xl text-muted-foreground leading-relaxed font-sans max-w-3xl">
            Explore residential transformations across Oakhurst, Winnona Park,
            Great Lakes, and Downtown Decatur. From custom kitchen renovations and
            spa master baths to finished basements and historic home restorations,
            every project is built with local craftsmanship and enduring materials.
          </motion.p>

          {/* Neighborhood footprint tags */}
          <motion.div variants={itemVariants} className="mt-6 flex flex-wrap items-center gap-2 pt-2">
            <span className="text-xs font-semibold tracking-wider uppercase text-muted-foreground mr-1">
              Active neighborhoods:
            </span>
            {localAreas.map((area, i) => <span key={area} data-index={i} className="inline-flex items-center rounded-full bg-secondary/80 px-2.5 py-0.5 text-xs font-medium text-foreground border border-border">
                {area}
              </span>)}
          </motion.div>

          {/* Filter summary badges & action row */}
          <motion.div variants={itemVariants} className="mt-10 pt-8 border-t border-border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-muted-foreground mb-3">
                Browse project domains
              </p>
              <div className="flex flex-wrap gap-2">
                {projectCategories.map((category, i) => <Badge key={category.label} data-index={i} variant={i === 0 ? "default" : "secondary"} className="px-3.5 py-1.5 text-xs font-medium cursor-default transition-colors">
                    <span>{category.label}</span>
                    <span className="ml-1 opacity-70">({category.count})</span>
                  </Badge>)}
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <Button asChild variant="outline" size="sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-1.5">
                  <span>Start your project</span>
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}